import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFilter,
  faPlus,
  faPencil,
  faTrash,
  faEllipsisV,
  faIndianRupeeSign,
  faCalendar,
  faEye,
} from "@fortawesome/free-solid-svg-icons"
import { Dropdown } from "react-bootstrap"
import imgs from "../../../Utils/assets/img/faces/5.jpg"

// Sample quotations data - replace with actual API data
const quotationsData = [
  {
    id: 1,
    quotationNumber: "QUO-2025-001",
    title: "CRM Development Project",
    amount: 650000,
    currency: "INR",
    status: "Sent",
    validUntil: "2025-02-15",
    createdBy: "Rahul Gupta",
    createdDate: "Jan 15, 2025",
    quotationDesc: "Do you want me to rewrite your stepper component properly with Bootstrap/React logic so it handles current, completed, and upcoming steps automatically?",
    items: [
      { name: "CRM Development", quantity: 1, rate: 500000, amount: 500000 },
      { name: "Training & Support", quantity: 1, rate: 150000, amount: 150000 },
    ],
  },
  {
    id: 2,
    quotationNumber: "QUO-2025-002",
    title: "Additional Features",
    amount: 200000,
    currency: "INR",
    status: "Draft",
    validUntil: "2025-02-10",
    createdBy: "Priya Sharma",
    quotationDesc: "Do you want me to rewrite your stepper component properly with Bootstrap/React logic so it handles current, completed, and upcoming steps automatically?",
    createdDate: "Jan 14, 2025",
    items: [{ name: "Mobile App Development", quantity: 1, rate: 200000, amount: 200000 }],
  },
  {
    id: 3,
    quotationNumber: "QUO-2025-003",
    title: "Maintenance Contract",
    amount: 120000,
    currency: "INR",
    status: "Accepted",
    validUntil: "2025-01-30",
    createdBy: "Neha Patel",
    quotationDesc: "Do you want me to rewrite your stepper component properly with Bootstrap/React logic so it handles current, completed, and upcoming steps automatically?",
    createdDate: "Jan 13, 2025",
    items: [{ name: "Annual Maintenance", quantity: 1, rate: 120000, amount: 120000 }],
  },
]

/**
 * QuotationComponent - Displays and manages quotations for a lead
 * @param {string} leadId - The ID of the lead
 */
export const Quotation = ({ leadId }) => {
  const [quotations, setQuotations] = useState(quotationsData)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showQuotationActions, setShowQuotationActions] = useState({})
  const [newQuotation, setNewQuotation] = useState({
    title: "",
    validUntil: "",
    items: [{ name: "", quantity: 1, rate: 0 }],
  })

  /**
   * Gets status badge color
   * @param {string} status - Quotation status
   */
  const getStatusColor = (status) => {
    switch (status) {
      case "Accepted":
        return "bg-success-transparent"
      case "Sent":
        return "bg-primary-transparent"
      case "Draft":
        return "bg-warning-transparent"
      case "Rejected":
        return "bg-danger-transparent"
      default:
        return "bg-secondary-transparent"
    }
  }
  const getStatusBgColor = (status) => {
    switch (status) {
      case "Accepted":
        return "#eefff9"
      case "Sent":
        return "#f9fcff"
      case "Draft":
        return "#fffcf6"
      case "Rejected":
        return "#fff9f7"
      default:
        return "#f7feff"
    }
  }

  /**
   * Formats currency amount
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code
   */
  const formatCurrency = (amount, currency = "INR") => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount)
  }

  /**
   * Handles adding new item to quotation
   */
  const addQuotationItem = () => {
    setNewQuotation({
      ...newQuotation,
      items: [...newQuotation.items, { name: "", quantity: 1, rate: 0 }],
    })
  }

  /**
   * Handles removing item from quotation
   * @param {number} index - Index of item to remove
   */
  const removeQuotationItem = (index) => {
    const items = newQuotation.items.filter((_, i) => i !== index)
    setNewQuotation({ ...newQuotation, items })
  }

  /**
   * Updates quotation item
   * @param {number} index - Index of item to update
   * @param {string} field - Field to update
   * @param {any} value - New value
   */
  const updateQuotationItem = (index, field, value) => {
    const items = [...newQuotation.items]
    items[index] = { ...items[index], [field]: value }
    setNewQuotation({ ...newQuotation, items })
  }

  /**
   * Calculates total amount for new quotation
   */
  const calculateTotal = () => {
    return newQuotation.items.reduce((total, item) => {
      return total + item.quantity * item.rate
    }, 0)
  }

  /**
   * Handles creating new quotation
   */
  const handleCreateQuotation = () => {
    if (newQuotation.title.trim() && newQuotation.validUntil) {
      const quotation = {
        id: Date.now(),
        quotationNumber: `QUO-2025-${String(quotations.length + 1).padStart(3, "0")}`,
        title: newQuotation.title,
        amount: calculateTotal(),
        currency: "INR",
        status: "Draft",
        validUntil: newQuotation.validUntil,
        createdBy: "Current User",
        createdDate: new Date().toLocaleDateString(),
        items: newQuotation.items.filter((item) => item.name.trim()),
      }
      setQuotations([quotation, ...quotations])
      setNewQuotation({
        title: "",
        validUntil: "",
        items: [{ name: "", quantity: 1, rate: 0 }],
      })
      setShowCreateForm(false)
    }
  }

  /**
   * Toggles quotation actions dropdown
   * @param {number} quotationId - ID of the quotation
   */
  const toggleQuotationActions = (quotationId) => {
    setShowQuotationActions((prev) => ({
      ...prev,
      [quotationId]: !prev[quotationId],
    }))
  }

  /**
   * Handles quotation view action
   * @param {number} quotationId - ID of the quotation to view
   */
  const handleViewQuotation = (quotationId) => {
    // Implement view functionality
    setShowQuotationActions({})
  }

  /**
   * Handles quotation edit action
   * @param {number} quotationId - ID of the quotation to edit
   */
  const handleEditQuotation = (quotationId) => {
    // Implement edit functionality
    setShowQuotationActions({})
  }

  /**
   * Handles quotation delete action
   * @param {number} quotationId - ID of the quotation to delete
   */
  const handleDeleteQuotation = (quotationId) => {
    setQuotations(quotations.filter((q) => q.id !== quotationId))
    setShowQuotationActions({})
  }
  return (
    <div>
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="card-title mb-0" style={{ fontSize: "19px" }}>
          Quotations
        </div>
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-default-outline" style={styles.filterbuttons}>
            <FontAwesomeIcon icon={faFilter} />
          </button>
          <button
            className="btn btn-default-outline"
            style={styles.buttons}
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>

      <hr />

      {/* Create Quotation Form */}
      {showCreateForm && (
        <div className="card mb-4" style={{ border: "1px solid #e0e4f8" }}>
          <div className="card-body p-3">
            <h6 className="card-title mb-3">Create New Quotation</h6>
            <div className="row mb-3">
              <div className="col-8">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Quotation title..."
                  value={newQuotation.title}
                  onChange={(e) => setNewQuotation({ ...newQuotation, title: e.target.value })}
                />
              </div>
              <div className="col-4">
                <input
                  type="date"
                  className="form-control"
                  placeholder="Valid until..."
                  value={newQuotation.validUntil}
                  onChange={(e) => setNewQuotation({ ...newQuotation, validUntil: e.target.value })}
                />
              </div>
            </div>

            <h6 className="mb-2">Items</h6>
            {newQuotation.items.map((item, index) => (
              <div key={index} className="row mb-2">
                <div className="col-5">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Item name..."
                    value={item.name}
                    onChange={(e) => updateQuotationItem(index, "name", e.target.value)}
                  />
                </div>
                <div className="col-2">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateQuotationItem(index, "quantity", Number.parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="col-3">
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={(e) => updateQuotationItem(index, "rate", Number.parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="col-2">
                  <button
                    className="btn btn-outline-danger btn-sm w-100"
                    onClick={() => removeQuotationItem(index)}
                    disabled={newQuotation.items.length === 1}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <button className="btn btn-outline-primary btn-sm" onClick={addQuotationItem}>
                <FontAwesomeIcon icon={faPlus} /> Add Item
              </button>
              <div className="fw-bold">Total: {formatCurrency(calculateTotal())}</div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleCreateQuotation}>
                Create Quotation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quotations List */}
      <div style={{ height: "calc(100vh - 10rem)", overflow: "auto" }}>
        {quotations.map((quotation) => (
          <div key={quotation.id} className="card mb-3" style={{ border: "1px solid #e5eaff", background: `${getStatusBgColor(quotation.status)}`}}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div><h4 className="mb-0" style={{ color: 'black', fontSize: 15, lineHeight: 0.7}}>{quotation.title}</h4>
                <small className="text-muted" style={{ fontSize: "11px" }}>
                 Created : {quotation.createdDate}
                </small>
           
{/* <p className="mb-3 fs-12">{quotation.quotationNumber}</p> */}
                 
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className={`badge ${getStatusColor(quotation.status)}`} style={{ fontSize: "10px", fontWeight: 500,borderRadius: '27px',padding: '4px 10px'}}>
                    {quotation.status}
                  </span>
                  <div className="position-relative dropdownsWithoutToggle">
                    <button
                      className="btn btn-sm btn-outline-none dropdown-toggle"
                      onClick={() => toggleQuotationActions(quotation.id)}
                      style={{ fontSize: "15px", padding: "2px 6px" }}
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </button>

                    {showQuotationActions[quotation.id] && (
                      <div
                        className="dropdown-menu show position-absolute"
                        style={{
                          right: 0,
                          top: "100%",
                          minWidth: "120px",
                          zIndex: 1000,
                        }}
                      >
                        <Dropdown.Item
                          onClick={() => handleViewQuotation(quotation.id)}
                          className="fs-11 d-flex gap-2 align-items-center"
                        >
                          <FontAwesomeIcon icon={faEye} /> View
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleEditQuotation(quotation.id)}
                          className="fs-11 d-flex gap-2 align-items-center"
                        >
                          <FontAwesomeIcon icon={faPencil} /> Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleDeleteQuotation(quotation.id)}
                          className="fs-11 d-flex gap-2 align-items-center text-danger"
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </Dropdown.Item>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div style={{display: 'flex',gap: '22px'}}><p className="mb-0" style={{color: 'black',fontSize: '12px',fontWeight: 600,    background: '#e4e4e4',color: '#3d3d3d',borderRadius: 21,padding: '1px 10px' }}><sanp style={{fontWeight: 400}}>Q.No : </sanp> {quotation.quotationNumber}</p>
                <small className=" d-flex align-items-center gap-1" style={{fontSize: 12,color: 'black',fontWeight: 600, background: '#ffe7de ',color: 'red',borderRadius: 21,padding: '1px 10px'}}> <span style={{fontSize: 11,fontWeight: 400}}> Valid Till : </span>{quotation.validUntil}</small></div>
<p style={{color: '#928f8f',fontSize: 13,marginBottom: 0,color:' #323234',fontWeight: 500,marginTop: 8}}>{quotation.quotationDesc}</p>
               <hr style={{margin: '8px 0px'}} />
                <div className="col">
                  <div className="d-flex align-items-center justify-content-between  gap-1">
                    {/* <FontAwesomeIcon icon={faIndianRupeeSign} style={{ fontSize: "12px", color: "#28a745" }} /> */}
                    
<div className="d-flex justify-content-between align-items-center gap-3">
  
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar avatar-sm">
                    <img
                      src={imgs || "/placeholder.svg"}
                      alt=""
                      style={{ width: "27px", height: "27px", borderRadius: "50%" }}
                    />
                  </div>
                  <small style={{ fontSize: "12px", color: '#888585' , fontWeight: 400}}>
                    {quotation.createdBy}
                  </small>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <span style={{fontSize: 11,fontWeight: 300}}>Budget : </span>
               <span className="fw-bold" style={{ fontSize: "14px", color: '#2d364a' }}>
                      {formatCurrency(quotation.amount, quotation.currency)}
                    </span>
              </div>
              </div>
                  </div>
                </div>
              
              </div>

              {/* <div className="mb-2">
                <small style={{ fontSize: "11px", color: "#666" }}>Items:</small>
                {quotation.items.map((item, index) => (
                  <div
                    key={index}
                    className="d-flex justify-content-between"
                    style={{ fontSize: "11px", color: "#666" }}
                  >
                    <span>{item.name}</span>
                    <span>
                      {item.quantity} × {formatCurrency(item.rate)} = {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))}
              </div> */}

              
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const styles = {
  filterbuttons: {
    fontSize: "13px",
    height: "33px",
    width: "33px",
    maxHeight: 33,
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  buttons: {
    color: "#fff",
    fontSize: "13px",
    height: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}
